import type { Monaco } from '@monaco-editor/react';
import type { IDisposable, languages } from 'monaco-editor';
import {
  RuleDictionary,
  RuleSuggestion,
  RuleSuggestionKind,
  buildRuleSuggestions,
  describeFunction,
  findActiveCall,
  signaturesOf
} from './ruleDictionary';

/**
 * The Monaco half of the rule editor: the rule language itself, its completion list and its
 * parameter hints.
 *
 * All of it is module scoped on purpose. Monaco is a singleton for the lifetime of the tab, and a
 * provider registered on it outlives the React component that registered it - so this used to
 * register one provider per mount, from inside a `.then()` that could resolve after the unmount
 * that was supposed to dispose it. Under StrictMode, which mounts every component twice, that meant
 * a provider nobody could ever dispose, answering from the dictionary it had captured: new fields
 * and new helpers went missing behind a stale copy, for the rest of the session and with no way to
 * clear it but a full reload. Hence one registration, reference counted, reading a dictionary the
 * mounted editor keeps up to date.
 */

/**
 * A language of its own rather than `javascript`, which is what the editor used to be opened with.
 * Monaco's JavaScript comes with the TypeScript worker attached, and that worker answers every
 * completion request too: `async`, `debugger`, `catch` and every identifier already in the buffer
 * arrived above the fields and the helpers, sorted ahead of them by a scorer nothing here can
 * reach. Registering a language the worker knows nothing about leaves the list to the dictionary.
 */
export const RULE_LANGUAGE_ID = 'jexl';

let liveDictionary: RuleDictionary = {};

let registration: { disposables: IDisposable[]; users: number } | null = null;

/** Point the registered providers at a newly loaded dictionary. Cheap, and safe to call on render. */
export const setRuleDictionary = (dictionary: RuleDictionary): void => {
  liveDictionary = dictionary;
};

/** The icon beside each row, which is most of what tells a field from a function at a glance. */
const iconFor = (monaco: Monaco, kind: RuleSuggestionKind) => {
  const kinds = monaco.languages.CompletionItemKind;

  switch (kind) {
    case 'namespace':
      return kinds.Module;
    case 'function':
      return kinds.Function;
    case 'object':
      return kinds.Struct;
    case 'field':
      return kinds.Field;
    case 'enum':
      return kinds.Enum;
    case 'enumMember':
      return kinds.EnumMember;
    case 'keyword':
      return kinds.Keyword;
    default:
      return kinds.Variable;
  }
};

const toCompletionItem = (
  monaco: Monaco,
  suggestion: RuleSuggestion,
  position: { lineNumber: number; column: number },
  word: { startColumn: number; endColumn: number }
): languages.CompletionItem => ({
  label: suggestion.label,
  kind: iconFor(monaco, suggestion.kind),
  insertText: suggestion.insertText,
  insertTextRules: suggestion.isSnippet
    ? monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet
    : undefined,
  detail: suggestion.detail,
  documentation: suggestion.documentation ? { value: suggestion.documentation } : undefined,
  sortText: suggestion.sortText,
  filterText: suggestion.filterText,
  range: {
    startLineNumber: position.lineNumber,
    endLineNumber: position.lineNumber,
    // A suggestion may reach back over what was already typed - the dot in `db.coalesce`, which is
    // rewritten into the colon the engine needs.
    startColumn: Math.max(1, word.startColumn - (suggestion.replaceCharsBefore ?? 0)),
    endColumn: word.endColumn
  },
  command: suggestion.retrigger
    ? { id: 'editor.action.triggerSuggest', title: 'Suggest' }
    : undefined
});

/**
 * Registers the rule language features, or joins the registration that is already there, and
 * answers with the release for this caller. The providers go away when the last editor does.
 */
export const acquireRuleLanguageFeatures = (monaco: Monaco): (() => void) => {
  if (registration) {
    registration.users += 1;
  } else {
    registration = { disposables: install(monaco), users: 1 };
  }

  let released = false;
  return () => {
    // Guarded: a release called twice would take the count below zero and dispose the providers
    // out from under an editor that is still on screen.
    if (released || !registration) return;
    released = true;

    registration.users -= 1;
    if (registration.users === 0) {
      registration.disposables.forEach((disposable) => disposable.dispose());
      registration = null;
    }
  };
};

/** JEXL's own words, for the highlighting. `and`, `or` and `not` are spellings of `&&`, `||`, `!`. */
const KEYWORDS = [
  'var', 'if', 'else', 'for', 'while', 'return', 'function', 'new',
  'null', 'true', 'false',
  'and', 'or', 'not', 'empty', 'size'
];

const install = (monaco: Monaco): IDisposable[] => {
  // register() is not disposable and does not need to be: an id is claimed once for the tab, and
  // everything that gives it meaning is in the list below, which is.
  if (!monaco.languages.getLanguages().some((language) => language.id === RULE_LANGUAGE_ID)) {
    monaco.languages.register({ id: RULE_LANGUAGE_ID });
  }

  return [
    monaco.languages.setLanguageConfiguration(RULE_LANGUAGE_ID, {
      comments: { lineComment: '//', blockComment: ['/*', '*/'] },
      brackets: [
        ['{', '}'],
        ['[', ']'],
        ['(', ')']
      ],
      autoClosingPairs: [
        { open: '{', close: '}' },
        { open: '[', close: ']' },
        { open: '(', close: ')' },
        { open: "'", close: "'", notIn: ['string'] },
        { open: '"', close: '"', notIn: ['string'] }
      ]
    }),

    monaco.languages.setMonarchTokensProvider(RULE_LANGUAGE_ID, {
      keywords: KEYWORDS,
      tokenizer: {
        root: [
          [/\/\/.*$/, 'comment'],
          [/\/\*/, 'comment', '@comment'],
          // `db:`, `time:` - the namespace of a call, coloured apart from the plain identifiers so
          // that a dot typed where a colon belongs is visible before the rule is ever run.
          [/[a-zA-Z_]\w*(?=\s*:[a-zA-Z_])/, 'type'],
          [/[a-zA-Z_]\w*/, { cases: { '@keywords': 'keyword', '@default': 'identifier' } }],
          [/\d+(\.\d+)?/, 'number'],
          [/'([^'\\]|\\.)*'/, 'string'],
          [/"([^"\\]|\\.)*"/, 'string'],
          [/[{}()[\]]/, '@brackets'],
          [/[<>=!&|+\-*/%?:;,.]/, 'delimiter'],
          [/\s+/, 'white']
        ],
        comment: [
          [/[^/*]+/, 'comment'],
          [/\*\//, 'comment', '@pop'],
          [/[/*]/, 'comment']
        ]
      }
    }),

    monaco.languages.registerCompletionItemProvider(RULE_LANGUAGE_ID, {
      // The colon matters as much as the dot here: `db:` is how every helper is reached.
      triggerCharacters: ['.', ':'],
      provideCompletionItems: (model, position) => {
        const textBeforeCursor = model.getValueInRange({
          startLineNumber: position.lineNumber,
          startColumn: 1,
          endLineNumber: position.lineNumber,
          endColumn: position.column
        });
        const word = model.getWordUntilPosition(position);

        return {
          suggestions: buildRuleSuggestions(liveDictionary, textBeforeCursor).map((suggestion) =>
            toCompletionItem(monaco, suggestion, position, word)
          )
        };
      }
    }),

    monaco.languages.registerSignatureHelpProvider(RULE_LANGUAGE_ID, {
      signatureHelpTriggerCharacters: ['(', ','],
      signatureHelpRetriggerCharacters: [','],
      provideSignatureHelp: (model, position) => {
        // From the top of the document, not the line: an argument list in these rules is regularly
        // wrapped over several lines.
        const textBeforeCursor = model.getValueInRange({
          startLineNumber: 1,
          startColumn: 1,
          endLineNumber: position.lineNumber,
          endColumn: position.column
        });

        const call = findActiveCall(textBeforeCursor);
        if (!call) return null;

        const overloads = signaturesOf(liveDictionary, call.namespace, call.name);
        if (!overloads.length) return null;

        const signatures = overloads.map((signature) => {
          const described = describeFunction(call.namespace, signature);
          return {
            label: `${call.namespace}:${described.label}`,
            documentation: { value: described.documentation },
            parameters: described.params.map((param) => ({
              label: param.name,
              documentation: { value: `*(${param.type})* ${param.description}` }
            }))
          };
        });

        // The overload that can still take the argument being typed, so the hint follows the
        // arity someone is heading for rather than sitting on the shortest one.
        const active = overloads.findIndex((signature) => signature.paramTypes.length > call.activeParameter);

        return {
          value: {
            signatures,
            activeSignature: active === -1 ? overloads.length - 1 : active,
            activeParameter: call.activeParameter
          },
          dispose: () => undefined
        };
      }
    })
  ];
};
